// composables/useDynamicSeo.ts
import { toValue, type Ref } from 'vue'

const NUXT_CONFIG_DEFAULTS = {
  title: 'Akademie výtvarných umění v Praze',
  description:
    'Akademie výtvarných umění v Praze (AVU) je veřejná vysoká umělecká škola zaměřená na vzdělávání ve volném umění, restaurování a architektuře.',
  ogTitle: 'Akademie výtvarných umění v Praze',
  ogDescription:
    'Akademie výtvarných umění v Praze (AVU) je veřejná vysoká umělecká škola zaměřená na vzdělávání ve volném umění, restaurování a architektuře.',
  ogImage: 'https://avu.cz/images/AVU_OG.webp',
  ogType: 'website',
  twitterCard: 'summary_large_image',
  twitterTitle: 'Akademie výtvarných umění v Praze',
  twitterDescription:
    'Akademie výtvarných umění v Praze (AVU) je veřejná vysoká umělecká škola zaměřená na vzdělávání ve volném umění, restaurování a architektuře.',
  twitterImage: '/favicon/android-chrome-512x512.png',
}

export const useDynamicSeo = (
  dataInput: Ref<any> | Record<string, any> | (() => any),
  extraHeadOptions: Record<string, any> = {},
) => {
  const { locale } = useI18n()

  const homepageKey = computed(() => `pqfetch:Homepage:${locale.value}:{}`)
  const { data: cachedHpData } = useNuxtData<{ homepage?: any }>(
    homepageKey.value,
  )

  const hpData = computed(() => cachedHpData.value?.homepage)
  const hpSeo = computed(() => hpData.value?.seo || {})

  const pageData = computed(() => toValue(dataInput))
  const seo = computed(() => pageData.value?.seo || {})

  if (Object.keys(extraHeadOptions).length > 0) {
    useHead(extraHeadOptions)
  }

  useSeoMeta({
    title: () =>
      seo.value.title?.[locale.value] ||
      pageData.value?.title ||
      hpSeo.value.title?.[locale.value] ||
      hpData.value?.title ||
      NUXT_CONFIG_DEFAULTS.title,

    description: () =>
      seo.value.description?.[locale.value] ||
      pageData.value?.perex ||
      hpSeo.value.description?.[locale.value] ||
      hpData.value?.perex ||
      NUXT_CONFIG_DEFAULTS.description,

    ogTitle: () =>
      seo.value.og_title?.[locale.value] ||
      seo.value.title?.[locale.value] ||
      pageData.value?.title ||
      hpSeo.value.og_title?.[locale.value] ||
      hpSeo.value.title?.[locale.value] ||
      hpData.value?.title ||
      NUXT_CONFIG_DEFAULTS.ogTitle,

    ogDescription: () =>
      seo.value.og_description?.[locale.value] ||
      seo.value.description?.[locale.value] ||
      pageData.value?.perex ||
      hpSeo.value.og_description?.[locale.value] ||
      hpSeo.value.description?.[locale.value] ||
      hpData.value?.perex ||
      NUXT_CONFIG_DEFAULTS.ogDescription,

    ogImage: () =>
      seo.value.image?.url ||
      pageData.value?.featuredImage?.[0]?.url ||
      hpSeo.value.image?.url ||
      hpData.value?.featuredImage?.[0]?.url ||
      NUXT_CONFIG_DEFAULTS.ogImage,

    ogType: () =>
      seo.value.og_type || hpSeo.value.og_type || NUXT_CONFIG_DEFAULTS.ogType,

    twitterCard: () =>
      seo.value.twitter_card ||
      hpSeo.value.twitter_card ||
      NUXT_CONFIG_DEFAULTS.twitterCard,

    twitterTitle: () =>
      seo.value.twitter_title?.[locale.value] ||
      seo.value.og_title?.[locale.value] ||
      seo.value.title?.[locale.value] ||
      pageData.value?.title ||
      hpSeo.value.twitter_title?.[locale.value] ||
      hpSeo.value.og_title?.[locale.value] ||
      hpSeo.value.title?.[locale.value] ||
      hpData.value?.title ||
      NUXT_CONFIG_DEFAULTS.twitterTitle,

    twitterDescription: () =>
      seo.value.twitter_description?.[locale.value] ||
      seo.value.og_description?.[locale.value] ||
      seo.value.description?.[locale.value] ||
      pageData.value?.perex ||
      hpSeo.value.twitter_description?.[locale.value] ||
      hpSeo.value.og_description?.[locale.value] ||
      hpSeo.value.description?.[locale.value] ||
      hpData.value?.perex ||
      NUXT_CONFIG_DEFAULTS.twitterDescription,

    twitterImage: () =>
      seo.value.twitter_image?.[locale.value] ||
      seo.value.image?.url ||
      pageData.value?.featuredImage?.[0]?.url ||
      hpSeo.value.twitter_image?.[locale.value] ||
      hpSeo.value.image?.url ||
      hpData.value?.featuredImage?.[0]?.url ||
      NUXT_CONFIG_DEFAULTS.twitterImage,

    robots: () => seo.value.robots || hpSeo.value.robots,
  })

  useHead(() => {
    const canonical =
      seo.value?.canonical_url?.[locale.value] ||
      hpSeo.value?.canonical_url?.[locale.value]
    return canonical ? { link: [{ rel: 'canonical', href: canonical }] } : {}
  })
}
